<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import axiosIns from '@/plugins/axios'
import CommsSuppressionManager from '@/@core/components/CommsSuppressionManager.vue'
import EntityPicker from '@/@core/components/admin/EntityPicker.vue'
import { smsDetail } from '@/utils/commsSmsDetail'

// ── Tabs (Outbox / Suppressions) ─────────────────────────────────────
const activeTab = ref('outbox')

const loading = ref(false)
const loadingMore = ref(false)
const messages = ref([])
const total = ref(0)
const stats = ref({})
const renderFailedByTemplate = ref([])
const meanSendLatencyMs = ref(null)
const retryRateLast24h = ref(null)
const page = ref(1)
const itemsPerPage = ref(50)
const loadMoreSentinel = ref(null)
let scrollObserver = null

// Increments on every fetch so stale responses (e.g. a slow loadMore that
// resolves after the user changed a filter) can drop their results instead
// of polluting the now-current list.
let fetchToken = 0

const hasMore = computed(() => messages.value.length < total.value)

// Filters
const searchEmail = ref('')

// Slack alerts are noisy and rarely actionable here, so default to hiding them.
// Operators can switch to "All channels" or any specific channel (incl. Slack) explicitly.
const HIDE_SLACK_VALUE = 'email,inApp,smsHandoff,sms'
const channelFilter = ref(HIDE_SLACK_VALUE)
const eventFilter = ref('')
const templateFilter = ref('')
const startDateFilter = ref('')
const endDateFilter = ref('')
const memberFilter = ref(null)
const reservationIdFilter = ref('')
const restaurantFilter = ref(null)
const groupFilter = ref(null)
const advancedOpen = ref(false)

// Sort
const sortBy = ref('createdAt')
const sortDir = ref('desc')

const searchDebounce = ref(null)
const snackbar = ref({ show: false, message: '', color: 'success' })

const activeStatus = ref('')

// Status rail — failure-first ordering, condensed labels for triage
const STATUS_RAIL = [
  { value: '',              label: 'All',           tone: 'neutral' },
  { value: 'failed',        label: 'Failed',        tone: 'danger'  },
  { value: 'render_failed', label: 'Render Failed', tone: 'danger'  },
  { value: 'dead',          label: 'Dead',          tone: 'danger'  },
  { value: 'bounced',       label: 'Bounced',       tone: 'danger'  },
  { value: 'complained',    label: 'Complaint',     tone: 'warn'    },
  { value: 'suppressed',    label: 'Suppressed',    tone: 'warn'    },
  { value: 'pending',       label: 'Pending',       tone: 'info'    },
  { value: 'processing',    label: 'Processing',    tone: 'info'    },
  { value: 'sent',          label: 'Sent',          tone: 'ok'      },
  { value: 'delivered',     label: 'Delivered',     tone: 'ok'      },
]

const TONE_COLOR = {
  danger: 'error',
  warn:   'warning',
  info:   'info',
  ok:     'success',
  neutral: 'primary',
}

const STATUS_CHIP_COLOR = {
  sent: 'success',
  delivered: 'success',
  bounced: 'error',
  complained: 'deep-orange',
  suppressed: 'warning',
  pending: 'info',
  processing: 'info',
  failed: 'error',
  dead: 'error',
  render_failed: 'deep-purple',
}

const channelOptions = [
  { title: 'All except Slack', value: HIDE_SLACK_VALUE },
  { title: 'All channels',     value: '' },
  { title: 'Email',            value: 'email' },
  { title: 'Slack',            value: 'slack' },
  { title: 'In-App',           value: 'inApp' },
  { title: 'SMS Handoff',      value: 'smsHandoff' },
  { title: 'SMS (RM AI text)', value: 'sms' },
]

const channelLabel = value => channelOptions.find(o => o.value === value)?.title || value

const sortFieldOptions = [
  { title: 'Created',  value: 'createdAt' },
  { title: 'Sent',     value: 'sentAt' },
  { title: 'Attempts', value: 'attempts' },
]

const sortDirOptions = [
  { title: 'Newest first', value: 'desc' },
  { title: 'Oldest first', value: 'asc' },
]

// ── View modal ──────────────────────────────────────────────
const viewDialog = ref(false)
const viewRow = ref(null)

function openView(row) {
  viewRow.value = row
  viewDialog.value = true
}

// ── Action loaders ─────────────────────────────────────────
const retryLoading = ref(false)
const discardLoading = ref(false)
const exporting = ref(false)
const exportProgress = ref(0)

async function retryRow(row) {
  if (!confirm(`Retry message ${row._id}?\n\nStatus: ${row.status}`)) return
  retryLoading.value = true
  try {
    await axiosIns.post(`/admin/comms/messages/${row._id}/retry`)
    showSnackbar('Retry queued', 'success')
    fetchMessages()
  } catch (err) {
    showSnackbar(err.response?.data?.error || 'Retry failed', 'error')
  } finally {
    retryLoading.value = false
  }
}

// ── Force Resend dialog ───────────────────────────────────────
const resendDialog = ref(false)
const resendRow = ref(null)
const resendOverride = ref('')
const resendLoading = ref(false)

function openResend(row) {
  resendRow.value = row
  resendOverride.value = ''
  resendDialog.value = true
}

async function submitResend() {
  resendLoading.value = true
  try {
    const body = {}
    if (resendOverride.value?.trim()) {
      body.recipientOverride = resendOverride.value.trim()
    }
    await axiosIns.post(`/admin/comms/messages/${resendRow.value._id}/resend`, body)
    resendDialog.value = false
    showSnackbar('Resend queued', 'success')
    fetchMessages()
  } catch (err) {
    showSnackbar(err.response?.data?.error || 'Resend failed', 'error')
  } finally {
    resendLoading.value = false
  }
}

// ── Mark Sent dialog ──────────────────────────────────────────
const markSentDialog = ref(false)
const markSentRow = ref(null)
const markSentNote = ref('')
const markSentLoading = ref(false)

function openMarkSent(row) {
  markSentRow.value = row
  markSentNote.value = ''
  markSentDialog.value = true
}

async function submitMarkSent() {
  markSentLoading.value = true
  try {
    await axiosIns.post(`/admin/comms/messages/${markSentRow.value._id}/mark-sent`, {
      note: markSentNote.value,
    })
    markSentDialog.value = false
    showSnackbar('Marked as sent', 'success')
    fetchMessages()
  } catch (err) {
    showSnackbar(err.response?.data?.error || 'Mark-sent failed', 'error')
  } finally {
    markSentLoading.value = false
  }
}

// ── Discard ────────────────────────────────────────────
async function discardRow(row) {
  if (!confirm(`Discard render_failed message ${row._id}? This will move it to dead-letter.`)) return
  discardLoading.value = true
  try {
    await axiosIns.post(`/admin/comms/messages/${row._id}/discard`)
    showSnackbar('Message discarded', 'warning')
    fetchMessages()
  } catch (err) {
    showSnackbar(err.response?.data?.error || 'Discard failed', 'error')
  } finally {
    discardLoading.value = false
  }
}

// ── Data fetching ─────────────────────────────────────────────
async function fetchMessages({ append = false } = {}) {
  if (!append) {
    page.value = 1
    loading.value = true
  } else {
    loadingMore.value = true
  }
  const currentToken = ++fetchToken
  try {
    const params = {
      ...buildFilterParams(),
      page: page.value,
      limit: itemsPerPage.value,
    }

    const res = await axiosIns.get('/admin/comms/messages', { params })

    // A newer fetch superseded us (e.g. user changed filters mid-load) — drop the
    // result so we don't append stale rows onto the new filter's list.
    if (currentToken !== fetchToken) return

    const docs = res.data.data.docs || []
    messages.value = append ? [...messages.value, ...docs] : docs
    total.value = res.data.data.total
    stats.value = res.data.data.stats || {}
  } catch (err) {
    if (currentToken === fetchToken) showSnackbar('Failed to load messages', 'error')
  } finally {
    // Appends are serialized by the loadMore guard, so always safe to clear.
    // Non-append loading flag belongs to the current fetch only — a stale
    // non-append must not flip off the newer fetch's spinner.
    if (append) loadingMore.value = false
    else if (currentToken === fetchToken) loading.value = false
  }
}

async function loadMore() {
  if (loading.value || loadingMore.value || !hasMore.value) return
  page.value += 1
  await fetchMessages({ append: true })

  // If the new rows still don't fill the viewport, the sentinel stays visible
  // and IntersectionObserver won't refire on its own — nudge it by re-observing.
  if (hasMore.value && scrollObserver && loadMoreSentinel.value) {
    scrollObserver.unobserve(loadMoreSentinel.value)
    scrollObserver.observe(loadMoreSentinel.value)
  }
}

async function fetchStats() {
  try {
    const params = {}
    if (channelFilter.value) params.channel = channelFilter.value
    const res = await axiosIns.get('/admin/comms/stats', { params })
    if (res.data.success) {
      stats.value = res.data.data.counts || {}
      renderFailedByTemplate.value = res.data.data.renderFailedByTemplate || []
      meanSendLatencyMs.value = res.data.data.meanSendLatencyMs ?? null
      retryRateLast24h.value = res.data.data.retryRateLast24h ?? null
    }
  } catch (_err) {
    // Non-critical — KPI strip degrades gracefully
  }
}

// ── CSV export ────────────────────────────────────────────────
// Honors the current filter/sort settings and downloads every matching row,
// not just the displayed page. We paginate against the same /messages endpoint
// at its max page size (200) to avoid touching the backend.
function escapeCsvField(value) {
  if (value == null) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`
  }

  return str
}

function buildFilterParams() {
  const params = {
    sortBy: sortBy.value,
    sortDir: sortDir.value,
  }
  if (activeStatus.value) params.status = activeStatus.value
  if (channelFilter.value) params.channel = channelFilter.value
  if (searchEmail.value) params.email = searchEmail.value
  if (eventFilter.value) params.event = eventFilter.value
  if (templateFilter.value) params.templateName = templateFilter.value
  if (startDateFilter.value) params.startDate = startDateFilter.value
  if (endDateFilter.value) params.endDate = endDateFilter.value
  if (memberFilter.value?._id) params.userId = memberFilter.value._id
  if (reservationIdFilter.value) params.reservationId = reservationIdFilter.value
  if (restaurantFilter.value?._id) params.restaurantId = restaurantFilter.value._id
  if (groupFilter.value?._id) params.groupId = groupFilter.value._id

  return params
}

function exportRecipient(row) {
  if (row.payload?.to) return row.payload.to
  if (row.payload?.slackChannel) return row.payload.slackChannel

  return row.audienceKey || ''
}

function exportError(row) {
  return row.renderError || row.lastError || ''
}

const EXPORT_COLUMNS = [
  { header: 'Created',         value: r => formatDateAbsolute(r.createdAt) },
  { header: 'Sent',            value: r => (r.sentAt ? formatDateAbsolute(r.sentAt) : '') },
  { header: 'Status',          value: r => r.status || '' },
  { header: 'Channel',         value: r => r.channel || '' },
  { header: 'Event',           value: r => r.event || '' },
  { header: 'Template',        value: r => r.templateName || '' },
  { header: 'Recipient',       value: exportRecipient },
  { header: 'Subject',         value: r => r.payload?.subject || '' },
  { header: 'Attempts',        value: r => r.attempts ?? 0 },
  { header: 'Manual Retries',  value: r => r.manualRetry ?? 0 },
  { header: 'Provider ID',     value: r => r.providerMessageId || '' },
  { header: 'Bounce Type',     value: r => r.bounceType || '' },
  { header: 'Bounce Subtype',  value: r => r.bounceSubType || '' },
  { header: 'Complaint Type',  value: r => r.complaintFeedbackType || '' },
  { header: 'Error',           value: exportError },
  { header: 'Message ID',      value: r => r._id || '' },
]

// Backend caps `limit` at 200, so we paginate. Cap total rows to keep the
// browser responsive — operators with bigger exports should narrow filters.
const EXPORT_PAGE_SIZE = 200
const EXPORT_MAX_ROWS = 50000

async function fetchAllForExport(baseParams) {
  const all = []
  let pageNum = 1
  let totalCount = Infinity

  while (all.length < totalCount && all.length < EXPORT_MAX_ROWS) {
    const res = await axiosIns.get('/admin/comms/messages', {
      params: { ...baseParams, page: pageNum, limit: EXPORT_PAGE_SIZE },
    })
    const data = res.data?.data
    if (!data) break
    const docs = data.docs || []
    totalCount = data.total ?? docs.length
    all.push(...docs)
    exportProgress.value = Math.min(100, Math.round((all.length / Math.max(totalCount, 1)) * 100))
    if (docs.length < EXPORT_PAGE_SIZE) break
    pageNum += 1
  }

  return { rows: all, totalCount }
}

function buildCsv(rows) {
  const headerRow = EXPORT_COLUMNS.map(c => escapeCsvField(c.header)).join(',')
  const dataRows = rows.map(row =>
    EXPORT_COLUMNS.map(c => escapeCsvField(c.value(row))).join(','),
  )

  // Leading BOM so Excel opens UTF-8 correctly.
  return '\uFEFF' + [headerRow, ...dataRows].join('\r\n')
}

function downloadCsv(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

async function exportMessages() {
  if (exporting.value) return
  exporting.value = true
  exportProgress.value = 0
  try {
    const { rows, totalCount } = await fetchAllForExport(buildFilterParams())

    if (!rows.length) {
      showSnackbar('No messages match the current filters', 'info')

      return
    }

    const csvContent = buildCsv(rows)
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
    downloadCsv(csvContent, `comms-outbox-${stamp}.csv`)

    const truncated = rows.length >= EXPORT_MAX_ROWS && totalCount > EXPORT_MAX_ROWS
    showSnackbar(
      truncated
        ? `Exported first ${rows.length} of ${totalCount} rows (limit reached — narrow filters for more)`
        : `Exported ${rows.length} row${rows.length === 1 ? '' : 's'}`,
      truncated ? 'warning' : 'success',
    )
  } catch (err) {
    showSnackbar(err.response?.data?.error || 'Export failed', 'error')
  } finally {
    exporting.value = false
    exportProgress.value = 0
  }
}

function clearFilters() {
  searchEmail.value = ''

  // Reset to the same default the page mounts with (hide Slack), not "All channels" —
  // dumping the operator into a noisier view than the default would be surprising.
  channelFilter.value = HIDE_SLACK_VALUE
  eventFilter.value = ''
  templateFilter.value = ''
  startDateFilter.value = ''
  endDateFilter.value = ''
  memberFilter.value = null
  reservationIdFilter.value = ''
  restaurantFilter.value = null
  groupFilter.value = null
  activeStatus.value = ''
  page.value = 1
  fetchMessages()
  fetchStats()
}

function applyFilters() {
  page.value = 1
  fetchMessages()
}

function onChannelChange() {
  page.value = 1
  fetchMessages()

  // Channel scope affects the KPI strip too, so refresh stats.
  fetchStats()
}

function onSearchChange() {
  clearTimeout(searchDebounce.value)
  searchDebounce.value = setTimeout(() => {
    page.value = 1
    fetchMessages()
  }, 300)
}

function setStatus(status) {
  activeStatus.value = status
  page.value = 1
  fetchMessages()
}

function onSortChange() {
  page.value = 1
  fetchMessages()
}

function formatDate(d) {
  if (!d) return '—'
  const date = new Date(d)
  const now = new Date()
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`

  return date.toLocaleDateString()
}

function formatDateAbsolute(d) {
  if (!d) return '—'

  return new Date(d).toLocaleString()
}

function formatLatency(ms) {
  if (ms == null) return '—'
  if (ms < 1000) return `${Math.round(ms)} ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`

  return `${(ms / 60000).toFixed(1)}m`
}

function recipient(row) {
  if (row.payload && row.payload.to) return row.payload.to
  if (row.payload?.slackChannel) return row.payload.slackChannel

  return row.audienceKey || '—'
}

function statCount(status) {
  if (!status) return total.value
  
  return stats.value[status] || 0
}

function canRetry(row) {
  return ['failed', 'dead', 'render_failed'].includes(row.status)
}

function canResend(row) {
  return row.status === 'sent'
}

function canMarkSent(row) {
  return row.status === 'processing'
}

function canDiscard(row) {
  return row.status === 'render_failed'
}

function showSnackbar(message, color = 'success') {
  snackbar.value = { show: true, message, color }
}

// ── In-app notification preview helpers (mirror Notifications.vue) ───
function inAppIcon(np) {
  if (!np) return false
  if (np.type === 'reservation') return 'mdi-silverware-variant'
  if (np.type === 'follow') return 'mdi-user'
  if (np.type === 'happening') return 'emojione-v1:lightning-mood'
  if (np.type === 'message') return 'mdi-message-text-outline'

  return false
}

function inAppImage(np) {
  if (np?.imageId) return mediaUrl(np.imageId, 'thumb')

  return false
}

function inAppAvatarText(value) {
  if (!value) return ''

  return value.split(' ').map(w => w.charAt(0).toUpperCase()).join('')
}

function inAppAvatarColor(np) {
  return np?.color && !inAppImage(np) ? np.color : 'error'
}

function payloadPreview(payload, channel) {
  if (!payload) return '—'
  if (channel === 'email' && payload.html) return '[HTML email]'
  if (channel === 'smsHandoff' && payload.html) return '[SMS handoff email]'
  if (channel === 'sms' && payload.text) return payload.text.slice(0, 120)
  if (payload.slackText) return payload.slackText.slice(0, 120)
  if (payload.inAppPayload) return JSON.stringify(payload.inAppPayload).slice(0, 120)
  if (payload.html) return '[HTML payload]'

  return JSON.stringify(payload).slice(0, 120)
}

// ── Active-filter chips ─────────────────────────────────
const activeFilterChips = computed(() => {
  const chips = []
  if (searchEmail.value) chips.push({ key: 'email', label: `Email: ${searchEmail.value}` })

  // Only show a chip when the channel filter differs from the default (hide Slack),
  // so the default state stays quiet.
  if (channelFilter.value !== HIDE_SLACK_VALUE) {
    chips.push({
      key: 'channel',
      label: `Channel: ${channelFilter.value ? channelLabel(channelFilter.value) : 'All channels'}`,
    })
  }
  if (eventFilter.value) chips.push({ key: 'event', label: `Event: ${eventFilter.value}` })
  if (templateFilter.value) chips.push({ key: 'template', label: `Template: ${templateFilter.value}` })
  if (startDateFilter.value) chips.push({ key: 'startDate', label: `From ${startDateFilter.value}` })
  if (endDateFilter.value) chips.push({ key: 'endDate', label: `To ${endDateFilter.value}` })
  if (memberFilter.value) {
    const m = memberFilter.value
    const name = `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email
    chips.push({ key: 'member', label: `Member: ${name}` })
  }
  if (restaurantFilter.value) chips.push({ key: 'restaurant', label: `Restaurant: ${restaurantFilter.value.name}` })
  if (groupFilter.value) chips.push({ key: 'group', label: `Group: ${groupFilter.value.name}` })
  if (reservationIdFilter.value) chips.push({ key: 'reservationId', label: `Reservation #${reservationIdFilter.value.slice(-6)}` })

  return chips
})

function removeChip(key) {
  if (key === 'email') searchEmail.value = ''
  else if (key === 'channel') channelFilter.value = HIDE_SLACK_VALUE
  else if (key === 'event') eventFilter.value = ''
  else if (key === 'template') templateFilter.value = ''
  else if (key === 'startDate') startDateFilter.value = ''
  else if (key === 'endDate') endDateFilter.value = ''
  else if (key === 'member') memberFilter.value = null
  else if (key === 'restaurant') restaurantFilter.value = null
  else if (key === 'group') groupFilter.value = null
  else if (key === 'reservationId') reservationIdFilter.value = ''
  applyFilters()
  if (key === 'channel') fetchStats()
}

// Read entity-id filters from URL on mount (so embedded panels can deep-link).
async function readQueryFilters() {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  if (params.get('email')) searchEmail.value = params.get('email')
  if (params.get('reservationId')) reservationIdFilter.value = params.get('reservationId')
  if (params.get('status')) activeStatus.value = params.get('status')
  if (params.get('event')) eventFilter.value = params.get('event')
  if (params.get('templateName')) templateFilter.value = params.get('templateName')
  if (params.get('tab')) activeTab.value = params.get('tab')

  // Stub-load entity objects from IDs in URL — friendlier than showing the raw ID
  if (params.get('userId')) memberFilter.value = { _id: params.get('userId'), firstName: '', lastName: '', email: '(loading…)' }
  if (params.get('restaurantId')) restaurantFilter.value = { _id: params.get('restaurantId'), name: '(loading…)' }
  if (params.get('groupId')) groupFilter.value = { _id: params.get('groupId'), name: '(loading…)' }

  // If any entity filters are present, surface the drawer so the operator can see them
  if (memberFilter.value || restaurantFilter.value || groupFilter.value || reservationIdFilter.value) {
    advancedOpen.value = true
  }
}

watch([memberFilter, restaurantFilter, groupFilter], () => applyFilters())

const needsAttentionCount = computed(() => {
  const s = stats.value || {}

  return (s.failed || 0) + (s.dead || 0) + (s.render_failed || 0) + (s.bounced || 0)
})

function failureClass(item) {
  if (['failed', 'dead', 'render_failed', 'bounced'].includes(item.status)) return 'is-failure'
  if (['complained', 'suppressed'].includes(item.status)) return 'is-warn'

  return ''
}

// ── Inline error helpers (failed / dead / render_failed rows) ───
const ERROR_VISIBLE_STATUSES = ['failed', 'dead', 'render_failed', 'bounced']

function shouldShowError(item) {
  if (!item) return false
  if (!ERROR_VISIBLE_STATUSES.includes(item.status)) return false

  return Boolean(item.lastError || item.renderError)
}

function errorSummary(item) {
  // render_failed rows surface renderError first (template/Liquid issues);
  // everything else prefers lastError (channel adapter / SES / network).
  const raw = item.status === 'render_failed'
    ? (item.renderError || item.lastError)
    : (item.lastError || item.renderError)
  if (!raw) return ''

  // Most adapter errors are multi-line stack traces — the first line is
  // almost always the useful summary (e.g. "MessageRejected: Address not verified").
  return String(raw).split(/\r?\n/)[0].trim()
}

function errorTitle(item) {
  // Hover tooltip surfaces the full error; click → modal still has the rest.
  return [item.renderError, item.lastError].filter(Boolean).join('\n\n— — —\n\n')
}

function setupScrollObserver() {
  if (scrollObserver) scrollObserver.disconnect()
  scrollObserver = new IntersectionObserver(entries => {
    if (entries[0].isIntersecting) loadMore()
  }, { rootMargin: '300px' })
  if (loadMoreSentinel.value) scrollObserver.observe(loadMoreSentinel.value)
}

// Re-observe when the sentinel element remounts (e.g. switching tabs).
watch(loadMoreSentinel, el => {
  if (!scrollObserver) return
  scrollObserver.disconnect()
  if (el) scrollObserver.observe(el)
})

onMounted(async () => {
  readQueryFilters()
  fetchStats()
  await fetchMessages()

  // Set up the observer after the first page lands so its initial
  // visibility check sees real layout — if the first 50 rows don't fill
  // the viewport, the sentinel will be in view and loadMore fires.
  setupScrollObserver()
})

onBeforeUnmount(() => {
  if (scrollObserver) {
    scrollObserver.disconnect()
    scrollObserver = null
  }
})
</script>

<template>
  <VContainer
    fluid
    class="comms-hub px-md-6"
  >
    <!-- ── Header ─────────────────────────────────────── -->
    <header class="comms-header">
      <div>
        <h1 class="comms-title">
          Comms Hub
        </h1>
        <p class="comms-subtitle">
          Triage outbox failures, retry messages, and manage the email suppression list.
        </p>
      </div>
      <div class="comms-header-actions">
        <VBtn
          v-if="activeTab === 'outbox'"
          variant="text"
          :loading="exporting"
          :disabled="loading"
          prepend-icon="mdi-download-outline"
          :title="`Export ${total} matching message${total === 1 ? '' : 's'} (honors current filters)`"
          @click="exportMessages"
        >
          Export CSV
        </VBtn>
        <VBtn
          variant="text"
          :loading="loading"
          prepend-icon="mdi-refresh"
          @click="fetchMessages(); fetchStats()"
        >
          Refresh
        </VBtn>
      </div>
    </header>

    <!-- ── Tabs ─────────────────────────────────────── -->
    <VTabs
      v-model="activeTab"
      color="primary"
      class="comms-tabs"
      density="compact"
    >
      <VTab value="outbox">
        Outbox
        <span class="tab-count">{{ total }}</span>
      </VTab>
      <VTab value="suppressions">
        Suppressions
      </VTab>
    </VTabs>

    <VWindow
      v-model="activeTab"
      class="mt-4"
    >
      <!-- ── OUTBOX TAB ─────────────────────────────────────── -->
      <VWindowItem value="outbox">
        <!-- KPI strip — three high-signal metrics + render-failed-by-template hint -->
        <section class="kpi-strip">
          <div class="kpi">
            <div class="kpi-label">
              Mean Send Latency
            </div>
            <div
              class="kpi-value"
              data-testid="stat-mean-latency"
            >
              {{ formatLatency(meanSendLatencyMs) }}
            </div>
            <div class="kpi-hint">
              Sent → delivered, last 24h
            </div>
          </div>
          <div class="kpi">
            <div class="kpi-label">
              Retry Rate
            </div>
            <div
              class="kpi-value"
              data-testid="stat-retry-rate"
            >
              <template v-if="retryRateLast24h == null">
                —
              </template>
              <template v-else>
                {{ retryRateLast24h }}<span class="kpi-unit">%</span>
              </template>
            </div>
            <div class="kpi-hint">
              Of sends attempted, last 24h
            </div>
          </div>
          <div
            class="kpi kpi--needs-attention"
            :class="{ 'is-clean': !needsAttentionCount }"
          >
            <div class="kpi-label">
              Needs Attention
            </div>
            <div class="kpi-value">
              {{ needsAttentionCount }}
            </div>
            <div class="kpi-hint">
              <template v-if="needsAttentionCount === 0">
                All clear
              </template>
              <template v-else>
                Failed · Dead · Render-failed · Bounced
              </template>
            </div>
          </div>
        </section>

        <!-- Render-failed-by-template breakdown — compact callout -->
        <VAlert
          v-if="renderFailedByTemplate.length"
          type="warning"
          density="compact"
          variant="tonal"
          class="render-fail-alert"
          icon="mdi-alert-octagon-outline"
        >
          <div
            class="d-flex align-center flex-wrap"
            style="gap:8px"
          >
            <strong>Render failures by template:</strong>
            <VChip
              v-for="t in renderFailedByTemplate"
              :key="t.templateName"
              size="x-small"
              color="warning"
              variant="flat"
              class="font-weight-medium"
            >
              {{ t.templateName || '(no template)' }} · {{ t.count }}
            </VChip>
          </div>
        </VAlert>

        <!-- Status rail — failure-first, count-integrated chips -->
        <div class="status-rail">
          <button
            v-for="opt in STATUS_RAIL"
            :key="opt.value || 'all'"
            type="button"
            class="status-chip"
            :class="[
              `tone-${opt.tone}`,
              {
                'is-active': activeStatus === opt.value,
                'is-zero': opt.value && statCount(opt.value) === 0,
              },
            ]"
            @click="setStatus(opt.value)"
          >
            <span class="status-chip-label">{{ opt.label }}</span>
            <span class="status-chip-count">{{ statCount(opt.value) }}</span>
          </button>
        </div>

        <!-- Filter bar: a single dominant search + a "More filters" toggle. -->
        <div class="filter-bar">
          <VTextField
            v-model="searchEmail"
            placeholder="Search by recipient email…"
            prepend-inner-icon="mdi-magnify"
            density="comfortable"
            variant="outlined"
            hide-details
            clearable
            class="filter-search"
            @update:model-value="onSearchChange"
          />
          <VBtn
            variant="outlined"
            color="default"
            class="filter-toggle"
            :prepend-icon="advancedOpen ? 'mdi-chevron-up' : 'mdi-tune-variant'"
            @click="advancedOpen = !advancedOpen"
          >
            More filters
            <span
              v-if="activeFilterChips.length > (searchEmail ? 1 : 0)"
              class="filter-toggle-count"
            >
              {{ activeFilterChips.length - (searchEmail ? 1 : 0) }}
            </span>
          </VBtn>
        </div>

        <!-- Advanced filter drawer -->
        <VExpandTransition>
          <div
            v-show="advancedOpen"
            class="filter-drawer"
          >
            <VRow dense>
              <VCol
                cols="12"
                md="6"
                lg="4"
              >
                <EntityPicker
                  v-model="memberFilter"
                  type="member"
                  label="Member"
                  placeholder="Search by name or email"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
                lg="4"
              >
                <EntityPicker
                  v-model="restaurantFilter"
                  type="restaurant"
                  label="Restaurant"
                  placeholder="Search restaurants"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
                lg="4"
              >
                <EntityPicker
                  v-model="groupFilter"
                  type="group"
                  label="Group"
                  placeholder="User group"
                />
              </VCol>

              <VCol
                cols="12"
                md="4"
              >
                <VSelect
                  v-model="channelFilter"
                  :items="channelOptions"
                  item-title="title"
                  item-value="value"
                  label="Channel"
                  density="compact"
                  variant="outlined"
                  hide-details
                  prepend-inner-icon="mdi-message-cog-outline"
                  @update:model-value="onChannelChange"
                />
              </VCol>
              <VCol
                cols="12"
                md="4"
              >
                <VTextField
                  v-model="eventFilter"
                  label="Event"
                  placeholder="reservation.submitted_general"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-flash-outline"
                  @update:model-value="onSearchChange"
                />
              </VCol>
              <VCol
                cols="12"
                md="4"
              >
                <VTextField
                  v-model="templateFilter"
                  label="Template"
                  placeholder="reservationConfirmedMember"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-file-document-outline"
                  @update:model-value="onSearchChange"
                />
              </VCol>

              <VCol
                cols="6"
                md="3"
              >
                <VTextField
                  v-model="startDateFilter"
                  label="From"
                  type="date"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:model-value="applyFilters"
                />
              </VCol>
              <VCol
                cols="6"
                md="3"
              >
                <VTextField
                  v-model="endDateFilter"
                  label="To"
                  type="date"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:model-value="applyFilters"
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VTextField
                  v-model="reservationIdFilter"
                  label="Reservation ID"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-pound"
                  @update:model-value="onSearchChange"
                />
              </VCol>
              <VCol
                cols="6"
                md="2"
              >
                <VSelect
                  v-model="sortBy"
                  :items="sortFieldOptions"
                  item-title="title"
                  item-value="value"
                  label="Sort by"
                  density="compact"
                  variant="outlined"
                  hide-details
                  @update:model-value="onSortChange"
                />
              </VCol>
              <VCol
                cols="6"
                md="1"
              >
                <VSelect
                  v-model="sortDir"
                  :items="sortDirOptions"
                  item-title="title"
                  item-value="value"
                  label="Order"
                  density="compact"
                  variant="outlined"
                  hide-details
                  @update:model-value="onSortChange"
                />
              </VCol>
            </VRow>
          </div>
        </VExpandTransition>

        <!-- Applied-filter chips -->
        <div
          v-if="activeFilterChips.length"
          class="applied-filters"
        >
          <VChip
            v-for="chip in activeFilterChips"
            :key="chip.key"
            size="small"
            closable
            variant="tonal"
            color="primary"
            class="applied-chip"
            @click:close="removeChip(chip.key)"
          >
            {{ chip.label }}
          </VChip>
          <VBtn
            size="small"
            variant="text"
            color="default"
            @click="clearFilters"
          >
            Clear all
          </VBtn>
        </div>

        <!-- Messages table -->
        <VCard
          class="messages-card"
          elevation="0"
        >
          <VDataTableServer
            :items="messages"
            :items-length="total"
            :loading="loading"
            :items-per-page="messages.length || itemsPerPage"
            :page="1"
            item-value="_id"
            class="messages-table"
            :headers="[
              { title: 'Status', key: 'status', width: 130 },
              { title: 'Channel', key: 'channel', width: 90 },
              { title: 'Event', key: 'event' },
              { title: 'Recipient', key: '_recipient', sortable: false },
              { title: 'Created', key: 'createdAt', width: 180 },
              { title: '', key: '_actions', sortable: false, width: 220, align: 'end' },
            ]"
          >
            <template #item="{ item }">
              <tr
                class="msg-row"
                :class="failureClass(item)"
              >
                <td>
                  <VChip
                    :color="STATUS_CHIP_COLOR[item.status] || 'grey'"
                    size="small"
                    label
                    class="status-pill"
                  >
                    {{ item.status }}
                  </VChip>
                </td>
                <td>
                  <span class="channel-tag">{{ item.channel }}</span>
                </td>
                <td>
                  <div class="event-cell">
                    <span class="event-name">{{ item.event || '—' }}</span>
                    <RouterLink
                      v-if="item.templateName"
                      :to="{ path: '/all-email-templates', query: { templateName: item.templateName } }"
                      class="event-template event-template-link"
                      title="Open template in editor"
                    >
                      {{ item.templateName }}
                    </RouterLink>
                  </div>
                </td>
                <td>
                  <span class="recipient-text">{{ recipient(item) }}</span>
                  <span
                    v-if="item.bounceType || item.complaintFeedbackType"
                    class="bounce-meta"
                  >
                    {{ item.bounceType || item.complaintFeedbackType }}{{ item.bounceSubType ? ` / ${item.bounceSubType}` : '' }}
                  </span>
                  <span
                    v-if="shouldShowError(item)"
                    class="error-meta"
                    :title="errorTitle(item)"
                  >
                    <VIcon
                      size="12"
                      icon="mdi-alert-circle-outline"
                      class="me-1"
                    />
                    {{ errorSummary(item) }}
                  </span>
                </td>
                <td>
                  <span
                    :title="formatDate(item.createdAt)"
                    class="text-medium-emphasis text-caption created-cell"
                  >
                    {{ formatDateAbsolute(item.createdAt) }}
                  </span>
                </td>
                <td class="text-right">
                  <div class="row-actions">
                    <VBtn
                      size="small"
                      variant="text"
                      density="comfortable"
                      icon="mdi-eye-outline"
                      title="View"
                      @click="openView(item)"
                    />
                    <VBtn
                      v-if="canRetry(item)"
                      size="small"
                      color="warning"
                      variant="tonal"
                      density="comfortable"
                      :loading="retryLoading"
                      @click="retryRow(item)"
                    >
                      Retry
                    </VBtn>
                    <VBtn
                      v-if="canResend(item)"
                      size="small"
                      color="info"
                      variant="tonal"
                      density="comfortable"
                      @click="openResend(item)"
                    >
                      Resend
                    </VBtn>
                    <VBtn
                      v-if="canMarkSent(item)"
                      size="small"
                      color="success"
                      variant="tonal"
                      density="comfortable"
                      @click="openMarkSent(item)"
                    >
                      Mark Sent
                    </VBtn>
                    <VMenu v-if="canDiscard(item)">
                      <template #activator="{ props }">
                        <VBtn
                          v-bind="props"
                          size="small"
                          variant="text"
                          density="comfortable"
                          icon="mdi-dots-vertical"
                        />
                      </template>
                      <VList density="compact">
                        <VListItem
                          prepend-icon="mdi-trash-can-outline"
                          title="Discard"
                          @click="discardRow(item)"
                        />
                      </VList>
                    </VMenu>
                  </div>
                </td>
              </tr>
            </template>
            <template #no-data>
              <div class="empty-state">
                <VIcon
                  size="40"
                  color="success"
                >
                  mdi-check-decagram-outline
                </VIcon>
                <div class="empty-title">
                  Nothing to triage
                </div>
                <div class="empty-hint">
                  No messages match the current filters.
                </div>
              </div>
            </template>
            <!-- Suppress built-in pagination footer — replaced by infinite scroll below. -->
            <template #bottom />
          </VDataTableServer>

          <!-- Infinite scroll sentinel -->
          <div
            ref="loadMoreSentinel"
            class="scroll-sentinel"
          >
            <VProgressCircular
              v-if="loadingMore"
              indeterminate
              color="primary"
              size="24"
            />
            <div
              v-else-if="!loading && messages.length && !hasMore"
              class="text-caption text-medium-emphasis"
            >
              All {{ total }} message{{ total === 1 ? '' : 's' }} loaded
            </div>
          </div>
        </VCard>
      </VWindowItem>

      <!-- ── SUPPRESSIONS TAB ───────────────────────────────── -->
      <VWindowItem value="suppressions">
        <CommsSuppressionManager />
      </VWindowItem>
    </VWindow>

    <!-- View modal -->
    <VDialog
      v-model="viewDialog"
      max-width="800"
    >
      <VCard
        v-if="viewRow"
        class="detail-card"
      >
        <VCardTitle
          class="d-flex align-center"
          style="gap:12px"
        >
          <VChip
            :color="STATUS_CHIP_COLOR[viewRow.status] || 'grey'"
            size="small"
            label
          >
            {{ viewRow.status }}
          </VChip>
          <span class="text-h6">Message Detail</span>
        </VCardTitle>
        <VCardText>
          <dl class="detail-grid">
            <div><dt>ID</dt><dd>{{ viewRow._id }}</dd></div>
            <div><dt>Event</dt><dd>{{ viewRow.event || '—' }}</dd></div>
            <div><dt>Channel</dt><dd>{{ viewRow.channel }}</dd></div>
            <div>
              <dt>Template</dt>
              <dd>
                <RouterLink
                  v-if="viewRow.templateName"
                  :to="{ path: '/all-email-templates', query: { templateName: viewRow.templateName } }"
                  class="template-link"
                  title="Open template in editor"
                >
                  {{ viewRow.templateName }}
                </RouterLink>
                <template v-else>
                  —
                </template>
              </dd>
            </div>
            <div><dt>Attempts</dt><dd>{{ viewRow.attempts }}</dd></div>
            <div><dt>Manual Retries</dt><dd>{{ viewRow.manualRetry || 0 }}</dd></div>
            <div><dt>Provider ID</dt><dd>{{ viewRow.providerMessageId || '—' }}</dd></div>
            <div><dt>Created</dt><dd>{{ formatDateAbsolute(viewRow.createdAt) }}</dd></div>
            <div><dt>Sent</dt><dd>{{ formatDateAbsolute(viewRow.sentAt) }}</dd></div>
          </dl>

          <div
            v-if="viewRow.renderError"
            class="detail-section detail-section--error"
          >
            <div class="detail-section-title">
              Render Error
            </div>
            <pre class="detail-pre">{{ viewRow.renderError }}</pre>
          </div>
          <div
            v-if="viewRow.lastError"
            class="detail-section"
          >
            <div class="detail-section-title">
              Last Error
            </div>
            <pre class="detail-pre">{{ viewRow.lastError }}</pre>
          </div>

          <div
            v-if="viewRow.payload"
            class="detail-section"
          >
            <div class="detail-section-title">
              Payload
            </div>
            <template v-if="viewRow.channel === 'email'">
              <div
                v-if="viewRow.payload.to"
                class="detail-row"
              >
                <span class="detail-row-label">To</span>
                <span>{{ viewRow.payload.to }}</span>
              </div>
              <div
                v-if="viewRow.payload.subject"
                class="detail-row"
              >
                <span class="detail-row-label">Subject</span>
                <span>{{ viewRow.payload.subject }}</span>
              </div>
              <div
                v-if="viewRow.payload.text"
                class="detail-row detail-row--block"
              >
                <span class="detail-row-label">Text</span>
                <pre class="detail-pre">{{ viewRow.payload.text }}</pre>
              </div>
              <div
                v-if="viewRow.payload.html"
                class="detail-row detail-row--block"
              >
                <span class="detail-row-label">HTML</span>
                <iframe
                  :srcdoc="viewRow.payload.html"
                  class="html-preview"
                  sandbox=""
                />
              </div>
            </template>
            <template v-else-if="viewRow.channel === 'slack'">
              <div
                v-if="viewRow.payload.slackChannel"
                class="detail-row"
              >
                <span class="detail-row-label">Channel</span>
                <span>{{ viewRow.payload.slackChannel }}</span>
              </div>
              <div
                v-if="viewRow.payload.slackText"
                class="detail-row detail-row--block"
              >
                <span class="detail-row-label">Text</span>
                <pre class="detail-pre">{{ viewRow.payload.slackText }}</pre>
              </div>
            </template>
            <template v-else-if="viewRow.channel === 'inApp'">
              <div
                v-if="viewRow.payload.inAppPayload"
                class="inapp-preview"
              >
                <div class="inapp-preview-header text-caption text-medium-emphasis">
                  Preview — how this appears in the user's notifications panel
                </div>
                <div class="inapp-notification d-flex align-start gap-3">
                  <VAvatar
                    size="40"
                    :color="inAppAvatarColor(viewRow.payload.inAppPayload)"
                    :image="inAppImage(viewRow.payload.inAppPayload) || undefined"
                    :icon="!inAppImage(viewRow.payload.inAppPayload) ? (inAppIcon(viewRow.payload.inAppPayload) || undefined) : undefined"
                    :variant="inAppImage(viewRow.payload.inAppPayload) ? undefined : 'tonal'"
                  >
                    <span
                      v-if="!inAppImage(viewRow.payload.inAppPayload)
                        && !inAppIcon(viewRow.payload.inAppPayload)
                        && viewRow.payload.inAppPayload.text"
                    >
                      {{ inAppAvatarText(viewRow.payload.inAppPayload.text) }}
                    </span>
                  </VAvatar>

                  <div
                    class="flex-grow-1"
                    style="min-width: 0;"
                  >
                    <h6 class="text-sm font-weight-medium mb-1">
                      {{ viewRow.payload.inAppPayload.title || '(no title)' }}
                    </h6>
                    <p
                      v-if="viewRow.payload.inAppPayload.subtitle"
                      class="text-xs mb-2"
                      style="letter-spacing: 0.4px; line-height: 18px;"
                    >
                      {{ viewRow.payload.inAppPayload.subtitle }}
                    </p>
                    <p
                      class="text-xs text-disabled mb-0"
                      style="letter-spacing: 0.4px; line-height: 18px;"
                    >
                      {{ formatDate(viewRow.createdAt) }}
                    </p>
                  </div>

                  <VIcon
                    color="primary"
                    size="10"
                    icon="ri-circle-fill"
                    class="mt-1"
                    title="Unread"
                  />
                </div>
                <div
                  v-if="viewRow.payload.inAppPayload.actionRouterPath"
                  class="inapp-action mt-2 text-caption text-medium-emphasis"
                >
                  <VIcon
                    size="14"
                    icon="mdi-cursor-default-click-outline"
                    class="me-1"
                  />
                  Clicks through to <code>{{ viewRow.payload.inAppPayload.actionRouterPath }}</code>
                  <span v-if="viewRow.payload.inAppPayload.actionLabel"> · "{{ viewRow.payload.inAppPayload.actionLabel }}"</span>
                </div>
              </div>
              <details class="mt-3">
                <summary
                  class="text-caption text-medium-emphasis"
                  style="cursor: pointer;"
                >
                  Show raw payload
                </summary>
                <pre
                  v-if="viewRow.payload.inAppPayload"
                  class="detail-pre mt-2"
                >{{ JSON.stringify(viewRow.payload.inAppPayload, null, 2) }}</pre>
              </details>
            </template>
            <template v-else-if="viewRow.channel === 'smsHandoff'">
              <VAlert
                type="info"
                density="compact"
                variant="tonal"
                class="mb-3"
              >
                SMS handoff is delivered as an email to the RM internal address so an admin can manually text the restaurant contact.
              </VAlert>
              <div
                v-if="viewRow.payload.to"
                class="detail-row"
              >
                <span class="detail-row-label">To</span>
                <span>{{ viewRow.payload.to }}</span>
              </div>
              <div
                v-if="viewRow.payload.subject"
                class="detail-row"
              >
                <span class="detail-row-label">Subject</span>
                <span>{{ viewRow.payload.subject }}</span>
              </div>
              <div
                v-if="viewRow.payload.text"
                class="detail-row detail-row--block"
              >
                <span class="detail-row-label">Text</span>
                <pre class="detail-pre">{{ viewRow.payload.text }}</pre>
              </div>
              <div
                v-if="viewRow.payload.html"
                class="detail-row detail-row--block"
              >
                <span class="detail-row-label">HTML</span>
                <iframe
                  :srcdoc="viewRow.payload.html"
                  class="html-preview"
                  sandbox=""
                />
              </div>
            </template>
            <!-- A text RM sent: the whole text, who it went to and what it cost (RM-392) -->
            <template v-else-if="viewRow.channel === 'sms'">
              <VAlert
                v-if="smsDetail(viewRow).notSent"
                type="warning"
                density="compact"
                variant="tonal"
                class="mb-3"
              >
                {{ smsDetail(viewRow).notSent }}
              </VAlert>
              <div
                v-if="smsDetail(viewRow).to"
                class="detail-row"
              >
                <span class="detail-row-label">To</span>
                <span>{{ smsDetail(viewRow).to }}</span>
              </div>
              <div
                v-if="smsDetail(viewRow).size"
                class="detail-row"
              >
                <span class="detail-row-label">Size</span>
                <span>{{ smsDetail(viewRow).size }}</span>
              </div>
              <div
                v-if="smsDetail(viewRow).text"
                class="detail-row detail-row--block"
              >
                <span class="detail-row-label">Text</span>
                <pre class="detail-pre detail-pre--message">{{ smsDetail(viewRow).text }}</pre>
              </div>
            </template>
            <template v-else>
              <div class="text-body-2">
                {{ payloadPreview(viewRow.payload, viewRow.channel) }}
              </div>
            </template>
          </div>

          <div
            v-if="viewRow.context && Object.keys(viewRow.context).length"
            class="detail-section"
          >
            <div class="detail-section-title">
              Raw Context (render_failed)
            </div>
            <pre class="detail-pre">{{ JSON.stringify(viewRow.context, null, 2).slice(0, 2000) }}</pre>
          </div>

          <div
            v-if="viewRow.meta"
            class="detail-section"
          >
            <div class="detail-section-title">
              Meta
            </div>
            <pre class="detail-pre">{{ JSON.stringify(viewRow.meta, null, 2) }}</pre>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="viewDialog = false"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Force Resend dialog -->
    <VDialog
      v-model="resendDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>Force Resend</VCardTitle>
        <VCardText>
          <VAlert
            type="info"
            density="compact"
            variant="tonal"
            class="mb-3"
          >
            This creates a new outbox row with a fresh idempotency key. The suppression check
            is <strong>bypassed</strong> for force resends — use with care.
          </VAlert>
          <p class="mb-3">
            Original recipient: <strong>{{ resendRow?.payload?.to || '—' }}</strong>
          </p>
          <VTextField
            v-model="resendOverride"
            label="Override recipient address"
            placeholder="Leave blank to resend to the original address"
            variant="outlined"
            density="comfortable"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="resendDialog = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="info"
            :loading="resendLoading"
            @click="submitResend"
          >
            Resend
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Mark Sent dialog -->
    <VDialog
      v-model="markSentDialog"
      max-width="450"
    >
      <VCard>
        <VCardTitle>Mark as Sent</VCardTitle>
        <VCardText>
          <VAlert
            type="warning"
            density="compact"
            variant="tonal"
            class="mb-3"
          >
            Use when a worker died mid-claim and the row is stuck in "processing". Only mark
            as sent if you have confirmed the message was actually delivered.
          </VAlert>
          <VTextField
            v-model="markSentNote"
            label="Reason / note (optional)"
            placeholder="Worker restarted, SES confirmed delivery"
            variant="outlined"
            density="comfortable"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="markSentDialog = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="success"
            :loading="markSentLoading"
            @click="submitMarkSent"
          >
            Mark Sent
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
    >
      {{ snackbar.message }}
    </VSnackbar>
  </VContainer>
</template>

<style scoped>
.comms-hub {
  max-width: 1400px;
  margin: 0 auto;
  padding-top: var(--ds-space-6);
}

/* ── Header ── */
.comms-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ds-space-4);
  margin-bottom: var(--ds-space-4);
}
.comms-title {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ds-text-strong);
  line-height: 1.1;
  margin: 0;
}
.comms-subtitle {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--ds-text-muted);
  max-width: 60ch;
}
.comms-header-actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-1);
  flex-shrink: 0;
}
.created-cell {
  white-space: nowrap;
}
.scroll-sentinel {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: var(--ds-space-3) 0;
}

/* ── Tabs ── */
.comms-tabs {
  border-bottom: 1px solid var(--ds-border-subtle);
}
.tab-count {
  display: inline-flex;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  background: var(--ds-surface-inset);
  color: var(--ds-text-muted);
  font-size: 12px;
  font-weight: 600;
}

/* ── KPI Strip ── */
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--ds-space-3);
  margin-bottom: var(--ds-space-4);
}
@media (max-width: 720px) {
  .kpi-strip { grid-template-columns: 1fr; }
}
.kpi {
  background: var(--ds-surface-card);
  border: 1px solid var(--ds-border-subtle);
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.kpi-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ds-text-muted);
}
.kpi-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--ds-text-strong);
  font-variant-numeric: tabular-nums;
}
.kpi-unit {
  font-size: 18px;
  font-weight: 600;
  color: var(--ds-text-secondary);
  margin-left: 2px;
}
.kpi-hint {
  font-size: 12px;
  color: var(--ds-text-muted);
}
.kpi--needs-attention:not(.is-clean) {
  border-color: #fbd0c9;
  background: linear-gradient(180deg, #fff6f4 0%, #fff 70%);
}
.kpi--needs-attention:not(.is-clean) .kpi-value {
  color: #c0392b;
}
.kpi--needs-attention.is-clean .kpi-value {
  color: var(--ds-brand-primary-dark);
}

/* ── Render-fail alert ── */
.render-fail-alert {
  margin-bottom: var(--ds-space-4);
  border-radius: 10px;
}

/* ── Status rail ── */
.status-rail {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: var(--ds-space-4);
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--ds-surface-card);
  border: 1px solid var(--ds-border-light);
  font-size: 13px;
  font-weight: 500;
  color: var(--ds-text-secondary);
  cursor: pointer;
  transition: all 120ms ease;
}
.status-chip:hover {
  border-color: var(--ds-border-strong);
  color: var(--ds-text-strong);
}
.status-chip:focus-visible {
  outline: 2px solid var(--ds-brand-primary);
  outline-offset: 1px;
}
.status-chip-count {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: 12px;
  padding: 1px 8px;
  border-radius: 10px;
  background: var(--ds-surface-inset);
  color: var(--ds-text-secondary);
}
.status-chip.is-zero {
  opacity: 0.55;
}
.status-chip.is-active {
  border-color: transparent;
  color: #fff;
}
.status-chip.is-active .status-chip-count {
  background: rgba(255,255,255,0.22);
  color: #fff;
}
.status-chip.tone-danger.is-active   { background: #c0392b; }
.status-chip.tone-warn.is-active     { background: #d97706; }
.status-chip.tone-info.is-active     { background: #2563eb; }
.status-chip.tone-ok.is-active       { background: #15803d; }
.status-chip.tone-neutral.is-active  { background: var(--ds-brand-primary-dark); }

/* Subtle dot accent for non-active failure chips so failures still pop */
.status-chip.tone-danger:not(.is-active):not(.is-zero)::before {
  content: '';
  width: 6px; height: 6px; border-radius: 50%;
  background: #c0392b;
  margin-right: -2px;
}
.status-chip.tone-warn:not(.is-active):not(.is-zero)::before {
  content: '';
  width: 6px; height: 6px; border-radius: 50%;
  background: #d97706;
  margin-right: -2px;
}

/* ── Filter bar ── */
.filter-bar {
  display: flex;
  align-items: center;
  gap: var(--ds-space-3);
  margin-bottom: var(--ds-space-3);
}
.filter-search {
  flex: 1;
  max-width: 480px;
}
.filter-toggle-count {
  display: inline-flex;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 10px;
  background: var(--ds-brand-primary-light);
  color: var(--ds-brand-primary-dark);
  font-size: 12px;
  font-weight: 700;
}

/* ── Filter drawer ── */
.filter-drawer {
  background: var(--ds-surface-subtle);
  border: 1px solid var(--ds-border-subtle);
  border-radius: 12px;
  padding: var(--ds-space-4);
  margin-bottom: var(--ds-space-4);
}

/* ── Applied chips ── */
.applied-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: var(--ds-space-3);
}
.applied-chip {
  font-weight: 500;
}

/* ── Table ── */
.messages-card {
  border: 1px solid var(--ds-border-subtle);
  border-radius: 12px;
  overflow: hidden;
}
.messages-table :deep(thead th) {
  background: var(--ds-surface-subtle);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--ds-text-muted);
}
.messages-table :deep(tbody tr) {
  transition: background 80ms ease;
}
.messages-table :deep(tbody tr:hover) {
  background: var(--ds-surface-inset);
}
.msg-row.is-failure td:first-child {
  box-shadow: inset 3px 0 0 0 #c0392b;
}
.msg-row.is-warn td:first-child {
  box-shadow: inset 3px 0 0 0 #d97706;
}
.status-pill {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.channel-tag {
  display: inline-block;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--ds-surface-inset);
  color: var(--ds-text-secondary);
  font-weight: 500;
}
.event-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.event-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ds-text-primary);
}
.event-template {
  font-size: 11px;
  color: var(--ds-text-muted);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.event-template-link {
  text-decoration: none;
  border-bottom: 1px dotted var(--ds-border-strong, var(--ds-text-muted));
  transition: color 120ms ease, border-color 120ms ease;
}
.event-template-link:hover {
  color: var(--ds-brand-primary-dark);
  border-bottom-color: var(--ds-brand-primary-dark);
}
.template-link {
  color: var(--ds-brand-primary-dark);
  text-decoration: none;
  border-bottom: 1px dotted currentColor;
}
.template-link:hover {
  text-decoration: none;
  opacity: 0.85;
}
.recipient-text {
  display: block;
  font-size: 13px;
  color: var(--ds-text-primary);
  font-variant-ligatures: none;
}
.bounce-meta {
  display: block;
  font-size: 11px;
  color: #c0392b;
  margin-top: 2px;
}
.error-meta {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #c0392b;
  margin-top: 2px;
  max-width: 380px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.row-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 56px 0;
}
.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-text-strong);
}
.empty-hint {
  font-size: 13px;
  color: var(--ds-text-muted);
}

/* ── Detail dialog ── */
.detail-card {
  border-radius: 14px;
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 24px;
  margin: 0 0 16px;
}
.detail-grid > div { min-width: 0; }
.detail-grid dt {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ds-text-muted);
  font-weight: 600;
  margin-bottom: 2px;
}
.detail-grid dd {
  margin: 0;
  font-size: 14px;
  color: var(--ds-text-primary);
  word-break: break-word;
}
.detail-section {
  margin-top: 16px;
  border-top: 1px solid var(--ds-border-subtle);
  padding-top: 12px;
}
.detail-section-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ds-text-muted);
  font-weight: 600;
  margin-bottom: 6px;
}
.detail-section--error .detail-section-title { color: #c0392b; }
.detail-row {
  display: flex;
  gap: 12px;
  align-items: baseline;
  margin-bottom: 6px;
  font-size: 13px;
}
.detail-row--block {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}
.detail-row-label {
  min-width: 84px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ds-text-muted);
  font-weight: 600;
}
.detail-pre {
  background: var(--ds-surface-inset);
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 220px;
  overflow-y: auto;
  color: var(--ds-text-primary);
  margin: 0;
}
/* A text is prose to read, not a payload to scan: the body face, words kept whole, and room for a long one (RM-392) */
.detail-pre--message {
  font-family: inherit;
  font-size: 13px;
  line-height: 1.45;
  word-break: normal;
  overflow-wrap: anywhere;
  max-height: 420px;
}

.html-preview {
  width: 100%;
  height: 320px;
  border: 1px solid var(--ds-border-light);
  border-radius: 8px;
  background: #fff;
}

/* ── In-app notification preview ── */
.inapp-preview {
  background: var(--ds-surface-card);
  border: 1px solid var(--ds-border-subtle);
  border-radius: 10px;
  padding: 12px 14px;
}
.inapp-preview-header {
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
}
.inapp-notification {
  background: var(--ds-surface-subtle);
  border-radius: 8px;
  padding: 12px;
}
.inapp-action {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.inapp-action code {
  background: var(--ds-surface-inset);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  margin: 0 2px;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
</route>
